import React, { useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Typography, Box, Divider, Table, TableHead,
  TableBody, TableRow, TableCell, Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

// ── Helpers ───────────────────────────────────────────────────────────────────

const KV = ({ label, value, highlight }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.35, borderBottom: '1px solid #f0f0f0' }}>
    <Typography variant="body2" sx={{ color: '#555', fontFamily: 'monospace', fontSize: 12 }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: highlight ? 800 : 600, fontFamily: 'monospace', fontSize: 12, color: highlight ? '#006060' : 'inherit' }}>
      {String(value ?? '—')}
    </Typography>
  </Box>
);

const Formula = ({ children }) => (
  <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, px: 1.5, py: 0.75, my: 0.75, fontFamily: 'monospace', fontSize: 12, color: '#333', whiteSpace: 'pre-wrap', direction: 'ltr', textAlign: 'left' }}>
    {children}
  </Box>
);

const HebrewNote = ({ children }) => (
  <Typography variant="body2" sx={{ color: '#555', mb: 0.5, lineHeight: 1.6 }}>{children}</Typography>
);

const StepHeader = ({ n, title }) => (
  <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 0.75, fontSize: 13 }}>
    שלב {n} — {title}
  </Typography>
);

// ── Component ────────────────────────────────────────────────────────────────

export default function DebugPanel({ debug }) {
  const [open, setOpen] = useState(false);
  const [step5Expanded, setStep5Expanded] = useState(false);

  if (!debug) return null;
  if (!debug.step1_inputs) return null;

  const {
    step1_inputs: s1 = {},
    step2_interestRate: s2 = {},
    step3_ageMonths: s3 = {},
    step4_securityPeriod: s4 = {},
    step5_loopSummary: s5 = { allMonths: [] },
    step6_final: s6 = {},
  } = debug;

  return (
    <Accordion
      expanded={open}
      onChange={() => setOpen(o => !o)}
      sx={{ mt: 2, border: '1px solid #ffe082', borderRadius: '8px !important', '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#fff8e1', borderRadius: '8px' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#b26a00' }}>
          פירוט החישוב (מצב בדיקה)
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2 }}>

        {/* ── Step 1: Input Summary ─────────────────────────────────────────── */}
        <StepHeader n={1} title="נתוני קלט" />
        <HebrewNote>הפרמטרים שהוזנו לחישוב:</HebrewNote>
        <Box sx={{ mb: 2 }}>
          <KV label="fundType"           value={s1?.fundType} />
          <KV label="retirementMonth"    value={s1?.retirementMonth} />
          <KV label="retirementYear"     value={s1?.retirementYear} />
          <KV label="memberBirthDate"    value={s1?.memberBirthDate} />
          <KV label="memberGender"       value={s1?.memberGender} />
          <KV label="maritalStatus"      value={s1?.maritalStatus} />
          {s1?.spouseBirthDate && <KV label="spouseBirthDate"  value={s1?.spouseBirthDate} />}
          {s1?.spouseBirthDate && <KV label="spouseGender"     value={s1?.spouseGender} />}
          <KV label="savingsAmount"      value={s1?.savingsAmount?.toLocaleString('en-US')} />
          <KV label="securityPeriod"     value={s1?.securityPeriod} />
          <KV label="advanceMonths"      value={s1?.advanceMonths} />
          <KV label="capitalizationYears" value={s1?.capitalizationYears} />
          <KV label="managementFee"               value={s1?.managementFee} />
          <KV label="pensionPaidForPreviousMonth"  value={String(s1?.pensionPaidForPreviousMonth ?? '—')} />
          <KV label="capitalizationIncludeMortality" value={String(s1?.capitalizationIncludeMortality ?? '—')} />
          <KV label="mortalityImprovementModel"    value={s1?.mortalityImprovementModel} />
        </Box>
        <Divider sx={{ mb: 1.5 }} />

        {/* ── Step 2: Net Monthly Rate ──────────────────────────────────────── */}
        <StepHeader n={2} title="ריבית נטו חודשית" />
        <HebrewNote>מחשבים את שיעור הריבית השנתי נקי מדמי ניהול, ומומר לשיעור חודשי:</HebrewNote>
        <Formula>
{`r_net_annual_raw = (1 + r_gross) / (1 + fee) - 1
             = (1 + ${s2?.annualRate}) / (1 + ${s2?.managementFee}) - 1
             = ${s2?.netAnnualRaw}

r_net_annual  = ROUND(r_net_annual_raw, 4)
             = ${s2?.netAnnual}

r_monthly     = (1 + r_net_annual)^(1/12) - 1
             = (1 + ${s2?.netAnnual})^(1/12) - 1
             = ${s2?.monthlyRate}`}
        </Formula>
        <Box sx={{ mb: 2 }}>
          <KV label="annualRate (r_gross)"  value={s2?.annualRate} />
          <KV label="managementFee (fee)"   value={s2?.managementFee} />
          <KV label="netAnnualRaw"          value={s2?.netAnnualRaw} />
          <KV label="netAnnual (rounded)"   value={s2?.netAnnual} highlight />
          <KV label="monthlyRate"           value={s2?.monthlyRate} highlight />
        </Box>
        <Divider sx={{ mb: 1.5 }} />

        {/* ── Step 3: Age in Months ─────────────────────────────────────────── */}
        <StepHeader n={3} title="גיל בחודשים" />
        <HebrewNote>
          גיל מחושב ביחס לחודש הפרישה. חודש הלידה = חודש שלאחר חודש הלידה הפועל (כלל "הראשון לחודש").
        </HebrewNote>
        <Formula>
{`calcMonth     = birthMonth + 1   (חודש ראשון לאחר לידה)
ageMonths     = (retirYear - birthYear) × 12 + (retirMonth - calcMonth)

עמית:  (${s3?.memberBirthDate}) → calcMonth=${s3?.memberCalcMonth}
       = (${s1?.retirementYear} - ${s3?.memberBirthDate?.substring(0,4)}) × 12 + (${s1?.retirementMonth} - ${s3?.memberCalcMonth})
       = ${s3?.memberAgeMonths} חודשים`}
{s3?.spouseBirthDate ? `\n\nבן/בת זוג: (${s3?.spouseBirthDate}) → calcMonth=${s3?.spouseCalcMonth}
       = (${s1?.retirementYear} - ${s3?.spouseBirthDate?.substring(0,4)}) × 12 + (${s1?.retirementMonth} - ${s3?.spouseCalcMonth})
       = ${s3?.spouseAgeMonths} חודשים` : ''}
        </Formula>
        <Box sx={{ mb: 2 }}>
          <KV label="memberBirthDate"    value={s3?.memberBirthDate} />
          <KV label="memberCalcMonth"    value={s3?.memberCalcMonth} />
          <KV label="memberAgeMonths"    value={s3?.memberAgeMonths} highlight />
          {s3?.spouseBirthDate && <KV label="spouseBirthDate"  value={s3?.spouseBirthDate} />}
          {s3?.spouseBirthDate && <KV label="spouseCalcMonth"  value={s3?.spouseCalcMonth} />}
          {s3?.spouseBirthDate && <KV label="spouseAgeMonths"  value={s3?.spouseAgeMonths} highlight />}
        </Box>
        <Divider sx={{ mb: 1.5 }} />

        {/* ── Step 4: Effective Security Period ────────────────────────────── */}
        <StepHeader n={4} title="תקופת הבטחה אפקטיבית" />
        <HebrewNote>תקופת הביטחון מוגבלת כך שהעמית לא יחרוג מגיל 87 (1,044 חודשים):</HebrewNote>
        <Formula>
{`maxAllowed    = 87 × 12 - memberAgeMonths
             = 1044 - ${s3?.memberAgeMonths}
             = ${s4?.maxAllowed}

effective     = min(requested, maxAllowed)
             = min(${s4?.requested}, ${s4?.maxAllowed})
             = ${s4?.effective}`}
        </Formula>
        <Box sx={{ mb: 2 }}>
          <KV label="requested"   value={s4?.requested} />
          <KV label="maxAllowed"  value={s4?.maxAllowed} />
          <KV label="effective"   value={s4?.effective} highlight />
        </Box>
        <Divider sx={{ mb: 1.5 }} />

        {/* ── Step 5: Cash Flow Loop ────────────────────────────────────────── */}
        <StepHeader n={5} title={`לולאת תזרים מזומנים — ${s5?.totalMonths} חודשים`} />
        <HebrewNote>לכל חודש t מחושב תזרים מהוון המשקלל הסתברות הישרדות העמית והאלמן/ה:</HebrewNote>
        <Formula>
{`v_t           = 1 / (1 + r_monthly)^t          (גורם היוון)

t < security: CF_t = v_t × 1                  (תקופת ביטחון — תמיד משלמים)
t ≥ security: CF_t = v_t × (S_p(t) + rate × S_w(t) × P_died(t))`}
        </Formula>
        <Box sx={{ overflowX: 'auto', mb: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                {[
                  { key: 't',                 tip: 't' },
                  { key: 'pensionerAge',       tip: 'גיל פנסיונר — חודשים' },
                  { key: 'spouseAge',          tip: 'גיל בן/בת זוג — חודשים' },
                  { key: 'discountFactor',    tip: 'גורם היוון' },
                  { key: 'pensionerSurvival', tip: 'שרידות פנסיונר' },
                  { key: 'spouseSurvival',    tip: 'שרידות בת/בן זוג — qs1 × שיפור' },
                  { key: 'widowSurvival',     tip: 'שרידות אלמנה — qs2 × שיפור' },
                  { key: 'cashFlow',          tip: 'תזרים' },
                  { key: 'widowCashFlow',     tip: 'תזרים אלמנה' },
                ].map(({ key, tip }) => (
                  <TableCell key={key} title={tip} sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 11, p: 0.75, whiteSpace: 'nowrap', cursor: 'help' }}>{key}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const allRows = s5?.allMonths ?? [];
                const visibleRows = step5Expanded ? allRows : allRows.slice(0, 5);
                const fmt8 = v => (v === '' || v == null) ? '' : Number(v).toFixed(8);
                return visibleRows.map((row, i) => {
                  const shifted = allRows[i - 1];
                  // age columns: 1-row shift (row 0=empty, row i≥1=allRows[i-1])
                  const pensionerAgeDisplay = i === 0 ? '' : allRows[i - 1].pensionerAgeMonths;
                  const spouseAgeDisplay    = i === 0 ? '' : allRows[i - 1].spouseAgeMonths;
                  // survival columns: 2-row shift (row 0=empty, row 1=1, row k≥2=data[k-2])
                  const pensionerSurvivalDisplay = i === 0 ? '' : i === 1 ? 1 : allRows[i - 2].pensionerSurvival;
                  const spouseSurvivalDisplay    = i === 0 ? '' : i === 1 ? 1 : allRows[i - 2].spouseSurvival;
                  const widowSurvivalDisplay     = i === 0 ? '' : i === 1 ? 1 : allRows[i - 2].widowSurvival;
                  return (
                    <TableRow key={row.t}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, p: 0.75 }}>{row.t}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, p: 0.75 }}>{pensionerAgeDisplay}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, p: 0.75 }}>{spouseAgeDisplay}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, p: 0.75 }}>{fmt8(row.discountFactor)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, p: 0.75 }}>{fmt8(pensionerSurvivalDisplay)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, p: 0.75 }}>{fmt8(spouseSurvivalDisplay)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, p: 0.75 }}>{fmt8(widowSurvivalDisplay)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, p: 0.75 }}>{fmt8(shifted?.cashFlow)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, p: 0.75 }}>{fmt8(shifted?.widowCashFlow)}</TableCell>
                    </TableRow>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </Box>
        <Button
          size="small"
          startIcon={step5Expanded ? <RemoveIcon /> : <AddIcon />}
          onClick={() => setStep5Expanded(e => !e)}
          sx={{ mt: 0.5, mb: 2, fontSize: 11, textTransform: 'none', color: 'primary.main' }}
        >
          {step5Expanded
            ? `הסתר (5 שורות)`
            : `הצג הכל (עד גיל 120) — ${(s5?.allMonths ?? []).length} שורות`}
        </Button>
        <Divider sx={{ mb: 1.5 }} />

        {/* ── Step 6: Final Result ──────────────────────────────────────────── */}
        <StepHeader n={6} title="תוצאה סופית — מקדם וקצבה" />
        <HebrewNote>המקדם = סכום תזרימים + חודשי מקדמה − חודשי היוון. הקצבה = חיסכון ÷ מקדם:</HebrewNote>
        <Formula>
{`coefficient   = ΣCF + advanceMonths - capitalizationYears × 12
             = ${s6?.sumCashFlows} + ${s6?.advanceMonths} - ${s6?.capitalizationMonths}
             = ${s6?.coefficient}

pension       = savingsAmount / coefficient
             = ${s6?.savingsAmount?.toLocaleString('en-US')} / ${s6?.coefficient}
             = ${s6?.monthlyPension?.toLocaleString('en-US')} ₪`}
        </Formula>
        <Box>
          <KV label="sumCashFlows"         value={s6?.sumCashFlows} />
          <KV label="advanceMonths"         value={s6?.advanceMonths} />
          <KV label="capitalizationMonths"  value={s6?.capitalizationMonths} />
          <KV label="coefficient"           value={s6?.coefficient} highlight />
          <KV label="savingsAmount"         value={s6?.savingsAmount?.toLocaleString('en-US')} />
          <KV label="monthlyPension (₪)"    value={s6?.monthlyPension?.toLocaleString('en-US')} highlight />
        </Box>

      </AccordionDetails>
    </Accordion>
  );
}

